// Central registry of every customizable surface-rendering color/opacity in
// the app. Each group corresponds to one assessment page; each item is a
// distinct surface type rendered there. `opacity` here always maps to
// whichever Leaflet style prop that surface already used (almost always
// fillOpacity, occasionally stroke opacity for line-only surfaces) — see
// each page's render code for how it's actually applied.
//
// Keys are namespaced `${group.key}.${item.key}` and used as-is for
// localStorage persistence — renaming a key here orphans any saved
// customization under the old name, so prefer adding new keys over renaming.
export const SURFACE_STYLE_GROUPS = [
    {
        key: 'ols', label: 'OLS — Surfaces', page: 'OLS',
        items: [
            { key: 'ihs', label: 'Inner Horizontal Surface', color: '#3A8AC4', opacity: 0.30 },
            { key: 'conical', label: 'Conical Surface', color: '#F5A623', opacity: 0.30 },
            { key: 'ohs', label: 'Outer Horizontal Surface', color: '#9B59B6', opacity: 0.30 },
            { key: 'strip', label: 'Runway Strip', color: '#2EC98B', opacity: 0.30 },
            { key: 'approach', label: 'Approach Surface', color: '#F0C040', opacity: 0.30 },
            { key: 'departure', label: 'Take-off Climb Surface', color: '#E84040', opacity: 0.30 },
            { key: 'transitional', label: 'Transitional Surface', color: '#1ABC9C', opacity: 0.30 },
            { key: 'inner_approach', label: 'Inner Approach (OFZ)', color: '#FF9F43', opacity: 0.35 },
            { key: 'balked_landing', label: 'Balked Landing (OFZ)', color: '#EE5253', opacity: 0.35 },
            { key: 'inner_transitional', label: 'Inner Transitional (OFZ)', color: '#00D2D3', opacity: 0.35 },
        ],
    },
    {
        key: 'ols_adg', label: 'OLS (ADG) — Core Surfaces', page: 'OLS (ADG)',
        items: [
            { key: 'strip', label: 'Strip (Ch. 3.4)', color: '#2266ee', opacity: 0.30 },
            { key: 'transitional', label: 'Transitional (4.2.2)', color: '#9b59b6', opacity: 0.15 },
            { key: 'horizontal', label: 'Horizontal Surface (4.3.2)', color: '#5BA8D9', opacity: 0.04 },
            { key: 'straight_in_lower', label: 'Straight-in Instrument — Lower (4.3.3)', color: '#f1c40f', opacity: 0.05 },
            { key: 'straight_in_upper', label: 'Straight-in Instrument — Upper (4.3.3)', color: '#e67e22', opacity: 0.05 },
            { key: 'approach', label: 'Approach (4.2.1)', color: '#ff9800', opacity: 0.18 },
        ],
    },
    {
        key: 'ols_adg_ofz', label: 'OLS (ADG) — Obstacle Free Zone', page: 'OLS (ADG)',
        items: [
            { key: 'inner_approach', label: 'Inner Approach (4.2.3)', color: '#e74c3c', opacity: 0.25 },
            { key: 'balked_landing', label: 'Balked Landing (4.2.5)', color: '#f39c12', opacity: 0.20 },
            { key: 'inner_transitional', label: 'Inner Transitional (4.2.4)', color: '#16a085', opacity: 0.12 },
        ],
    },
    {
        key: 'ols_adg_pa', label: 'OLS (ADG) — Precision Approach', page: 'OLS (ADG)',
        items: [
            { key: 'approach_component', label: 'Precision: Approach Component', color: '#3498db', opacity: 0.08 },
            { key: 'missed_approach_component', label: 'Precision: Missed Approach Component', color: '#8e44ad', opacity: 0.08 },
            { key: 'lower_component', label: 'Precision: Lower Component', color: '#2ecc71', opacity: 0.20 },
            { key: 'transitional_component', label: 'Precision: Transitional Component', color: '#95a5a6', opacity: 0.05 },
        ],
    },
    {
        key: 'ols_adg_dep', label: 'OLS (ADG) — Departure', page: 'OLS (ADG)',
        items: [
            { key: 'instrument_departure', label: 'Instrument Departure (4.3.5)', color: '#1abc9c', opacity: 0.06 },
            { key: 'takeoff_climb', label: 'Take-off Climb (4.3.6)', color: '#e91e63', opacity: 0.06 },
        ],
    },
    {
        key: 'pans_ops', label: 'PA — ILS Surfaces', page: 'PA',
        items: [
            { key: 'ground', label: 'Ground Surface', color: '#FF6B6B', opacity: 0.45 },
            { key: 'w', label: 'W Surface', color: '#4ECDC4', opacity: 0.45 },
            { key: 'x', label: 'X Surface', color: '#45B7D1', opacity: 0.45 },
            { key: 'y', label: 'Y Surface', color: '#F4D03F', opacity: 0.45 },
            { key: 'z', label: 'Z Surface', color: '#9B59B6', opacity: 0.45 },
        ],
    },
    {
        key: 'npa', label: 'NPA — Procedure Segments', page: 'NPA',
        items: [
            { key: 'initial', label: 'Initial Segment', color: '#4A90E2', opacity: 0.12 },
            { key: 'intermediate', label: 'Intermediate Segment', color: '#F5A623', opacity: 0.12 },
            { key: 'final', label: 'Final Segment', color: '#7ED321', opacity: 0.12 },
            { key: 'missed', label: 'Missed Approach Segment', color: '#9B59B6', opacity: 0.12 },
        ],
    },
    {
        key: 'vss', label: 'VSS — Visual Segment Surface', page: 'VSS',
        items: [
            { key: 'surface', label: 'Visual Segment Surface', color: '#F4A300', opacity: 0.45 },
        ],
    },
    {
        key: 'circling', label: 'Circling — Aircraft Categories', page: 'Circling',
        items: [
            { key: 'a', label: 'Category A', color: '#00C800', opacity: 0.20 },
            { key: 'b', label: 'Category B', color: '#0096FF', opacity: 0.20 },
            { key: 'c', label: 'Category C', color: '#FFC800', opacity: 0.20 },
            { key: 'd', label: 'Category D', color: '#FF7800', opacity: 0.20 },
            { key: 'e', label: 'Category E', color: '#DC0000', opacity: 0.20 },
        ],
    },
    {
        key: 'sid_ois', label: 'SID OIS — Departure', page: 'SID OIS',
        items: [
            { key: 'track', label: 'Track', color: '#1a1aff', opacity: 0 },
            { key: 'primary', label: 'Primary Area', color: '#2266ee', opacity: 0.45 },
            { key: 'secondary', label: 'Secondary Area', color: '#ff9900', opacity: 0.35 },
            { key: 'waypoint', label: 'Waypoint', color: '#00c8a0', opacity: 0.9 },
            { key: 'ois_boundary', label: 'OIS Boundary', color: '#E84040', opacity: 0.45 },
        ],
    },
    {
        key: 'star_ois', label: 'STAR OIS — Arrival', page: 'STAR OIS',
        items: [
            { key: 'track', label: 'Track', color: '#1a1aff', opacity: 0 },
            { key: 'primary', label: 'Primary Area', color: '#2266ee', opacity: 0.45 },
            { key: 'secondary', label: 'Secondary Area', color: '#ff9900', opacity: 0.35 },
        ],
    },
    {
        key: 'msa', label: 'MSA / TAA', page: 'MSA',
        items: [
            { key: 'boundary', label: 'MSA/TAA 30NM Boundary', color: '#3498db', opacity: 0.19 },
        ],
    },
    {
        key: 'rnav', label: 'RNAV Routes', page: 'RNAV',
        items: [
            { key: 'route_active', label: 'Active Route', color: '#00e1ff', opacity: 0.9 },
            { key: 'route_inactive', label: 'Inactive Route', color: '#0a5d93', opacity: 0.9 },
            { key: 'buffer_primary', label: 'Primary Area (2.885 NM, full MOC)', color: '#2266ee', opacity: 0.30 },
            { key: 'buffer_secondary', label: 'Secondary Area (2.885 NM, reducing MOC)', color: '#ff9900', opacity: 0.20 },
        ],
    },
    {
        key: 'airspace', label: 'Airspace Assessment', page: 'Airspace',
        items: [
            { key: 'buffer', label: 'Assessment Buffer', color: '#5BA8D9', opacity: 0.04 },
        ],
    },
    {
        key: 'rmac', label: 'RMAC', page: 'RMAC',
        items: [
            { key: 'range_ring', label: '55 NM Range Ring', color: '#3498db', opacity: 1 },
            { key: 'sector', label: 'RMAC Sector', color: '#e74c3c', opacity: 0.12 },
        ],
    },
    {
        key: 'cns_bra', label: 'CNS BRA', page: 'CNS',
        items: [
            { key: 'ground_plane', label: 'Zone 1 — Ground Plane', color: '#e84040', opacity: 0.40 },
            { key: 'side_area', label: 'Zone 2 — Side Area', color: '#f5a623', opacity: 0.30 },
            { key: 'sloping_surface', label: 'Zone 3 — Sloping Surface', color: '#3a8ac4', opacity: 0.22 },
            { key: 'r_cylinder', label: 'r-cylinder — Primary Protection', color: '#e84040', opacity: 0.35 },
            { key: 'r_cone', label: 'r-cone — Secondary Protection', color: '#f5a623', opacity: 0.25 },
            { key: 'j_cylinder', label: 'j-cylinder — Wind Turbine Protection', color: '#3a8ac4', opacity: 0.15 },
        ],
    },
];

export const DEFAULT_SURFACE_STYLES = SURFACE_STYLE_GROUPS.reduce((acc, group) => {
    group.items.forEach(item => {
        acc[`${group.key}.${item.key}`] = { color: item.color, opacity: item.opacity };
    });
    return acc;
}, {});
